'use client';

import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Check, ChevronDown, ChevronRight, Factory, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import type { NaceCode } from '../searchSidebarOptions';

interface IndustryFilterGroupProps {
  t: TFunction<'translation', undefined>;
  industryCount: number;
  clearIndustry: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  lockOf: (control: string) => 'user' | 'pro' | null;
  industryOpen: boolean;
  setIndustryOpen: Dispatch<SetStateAction<boolean>>;
  selectedIndustries: string[];
  allIndustries: { key: string; label: string }[];
  toggleIndustry: (key: string) => void;
  facetCount: (facet: string, value: string) => number | undefined;
  hideNaceCodes: boolean;
  naceCodesOpen: boolean;
  setNaceCodesOpen: Dispatch<SetStateAction<boolean>>;
  setNaceSearch: Dispatch<SetStateAction<string>>;
  selectedNaceCodes: string[];
  naceCodes: NaceCode[];
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  setSelectedNaceCodes: Dispatch<SetStateAction<string[]>>;
  naceSearch: string;
  naceListRef: RefObject<HTMLDivElement | null>;
  filteredNaceCodes: NaceCode[];
  naceCodesHaveChildren: Set<string>;
  expandedNaceNodes: Set<string>;
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  toggleNaceExpanded: (code: string) => void;
  tagsOpen: boolean;
  setTagsOpen: Dispatch<SetStateAction<boolean>>;
  setTagSearch: Dispatch<SetStateAction<string>>;
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  tagSearch: string;
  tagList: string[];
  cpvCodesOpen: boolean;
  setCpvCodesOpen: Dispatch<SetStateAction<boolean>>;
  setCpvSearch: Dispatch<SetStateAction<string>>;
  cpvCode: string;
  cpvCodesList: { code: string; description: string }[];
  setCpvCode: Dispatch<SetStateAction<string>>;
  cpvSearch: string;
  cpvListRef: RefObject<HTMLDivElement | null>;
}

export function IndustryFilterGroup({
  t,
  industryCount,
  clearIndustry,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  lockOf,
  industryOpen,
  setIndustryOpen,
  selectedIndustries,
  allIndustries,
  toggleIndustry,
  facetCount,
  hideNaceCodes,
  naceCodesOpen,
  setNaceCodesOpen,
  setNaceSearch,
  selectedNaceCodes,
  naceCodes,
  removeSet,
  setSelectedNaceCodes,
  naceSearch,
  naceListRef,
  filteredNaceCodes,
  naceCodesHaveChildren,
  expandedNaceNodes,
  toggleSet,
  toggleNaceExpanded,
  tagsOpen,
  setTagsOpen,
  setTagSearch,
  tags,
  setTags,
  tagSearch,
  tagList,
  cpvCodesOpen,
  setCpvCodesOpen,
  setCpvSearch,
  cpvCode,
  cpvCodesList,
  setCpvCode,
  cpvSearch,
  cpvListRef,
}: IndustryFilterGroupProps) {
  return (
    <FilterGroup
      id="industry"
      icon={Factory}
      label={t('search.sidebar.groups.industry', {
        defaultValue: 'Industry & activity',
      })}
      activeCount={industryCount}
      onClear={clearIndustry}
      open={openFilterGroup === 'industry'}
      onOpenChange={handleGroupOpenChange('industry')}
      lock={groupLock('industry')}
    >
      {/* High-level Industry filter (maps to NACE divisions) */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('industries')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.industry', { defaultValue: 'Industry' })}
        </FilterLabel>
        <Popover
          open={lockOf('industries') ? false : industryOpen}
          onOpenChange={setIndustryOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('industries')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {selectedIndustries.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.industryPlaceholder', {
                      defaultValue: 'Pick industry',
                    })}
                  </span>
                ) : (
                  selectedIndustries.map((key) => {
                    const cat = allIndustries.find((c) => c.key === key);
                    return (
                      <Badge
                        key={key}
                        variant="secondary"
                        className="text-xs font-normal gap-1 pr-1 border border-foreground"
                      >
                        <span className="max-w-[220px] truncate">
                          {cat?.label ?? key}
                        </span>
                        <X
                          size={12}
                          className="cursor-pointer hover:bg-muted rounded-sm"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            toggleIndustry(key);
                          }}
                        />
                      </Badge>
                    );
                  })
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput
                className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                placeholder={t('search.sidebar.searchIndustries', {
                  defaultValue: 'Search industries…',
                })}
              />
              <CommandList className="max-h-[272px] overflow-y-auto">
                <CommandEmpty>
                  {t('search.sidebar.noIndustryFound', {
                    defaultValue: 'No industry found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {allIndustries.map(({ key, label }) => (
                    <CommandItem
                      key={key}
                      value={label}
                      onSelect={() => toggleIndustry(key)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedIndustries.includes(key)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="truncate text-xs">{label}</span>
                      <FacetCount count={facetCount('industry', key)} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* WZ 2025 Code (sector tree picker) */}
      {!hideNaceCodes && (
        <div className="space-y-1.5">
          <FilterLabel lock={lockOf('naceCodes')} className={FIELD_LABEL_CLASS}>
            {t('search.sidebar.naceCode', { defaultValue: 'WZ 2025 code' })}
          </FilterLabel>
          <Popover
            open={lockOf('naceCodes') ? false : naceCodesOpen}
            onOpenChange={(open) => {
              setNaceCodesOpen(open);
              if (!open) setNaceSearch('');
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={!!lockOf('naceCodes')}
                className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
              >
                <div className="flex flex-wrap gap-1 w-full">
                  {selectedNaceCodes.length === 0 ? (
                    <span className={FIELD_PLACEHOLDER_CLASS}>
                      {t('search.sidebar.industriesPlaceholder')}
                    </span>
                  ) : (
                    selectedNaceCodes.map((code) => {
                      const nace = naceCodes.find((c) => c.code === code);
                      return (
                        <Badge
                          key={code}
                          variant="secondary"
                          className="text-xs font-normal gap-1 pr-1 border border-foreground"
                        >
                          <span className="font-mono text-muted-foreground">
                            {code}
                          </span>
                          <span className="max-w-[220px] truncate">
                            {nace?.description || ''}
                          </span>
                          <X
                            size={12}
                            className="cursor-pointer hover:bg-muted rounded-sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              removeSet(code, setSelectedNaceCodes);
                            }}
                          />
                        </Badge>
                      );
                    })
                  )}
                  <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                  placeholder={t('search.sidebar.searchSectors')}
                  value={naceSearch}
                  onValueChange={(v) => {
                    setNaceSearch(v);
                    if (naceListRef.current) naceListRef.current.scrollTop = 0;
                  }}
                />
                <CommandList
                  ref={naceListRef}
                  className="h-[272px] overflow-y-auto"
                >
                  <CommandEmpty>
                    {t('search.sidebar.noSectorFound')}
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredNaceCodes.map((nace) => {
                      const inTreeMode = naceSearch.trim().length < 2;
                      const hasChildren = naceCodesHaveChildren.has(nace.code);
                      const isExpanded = expandedNaceNodes.has(nace.code);
                      const indent = inTreeMode ? (nace.level - 1) * 12 : 0;
                      return (
                        <CommandItem
                          key={nace.code}
                          value={`${nace.code} ${nace.description}`}
                          onSelect={() =>
                            toggleSet(nace.code, setSelectedNaceCodes)
                          }
                        >
                          <div
                            className="flex items-center w-full"
                            style={{ paddingLeft: indent }}
                          >
                            {inTreeMode && hasChildren ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleNaceExpanded(nace.code);
                                }}
                                aria-label={
                                  isExpanded
                                    ? t('search.sidebar.collapse', {
                                        defaultValue: 'Collapse',
                                      })
                                    : t('search.sidebar.expand', {
                                        defaultValue: 'Expand',
                                      })
                                }
                                className="mr-1 flex h-4 w-4 items-center justify-center rounded hover:bg-muted shrink-0"
                              >
                                <ChevronRight
                                  size={12}
                                  className={cn(
                                    'transition-transform',
                                    isExpanded && 'rotate-90',
                                  )}
                                />
                              </button>
                            ) : (
                              <span className="mr-1 inline-block h-4 w-4 shrink-0" />
                            )}
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4 shrink-0',
                                selectedNaceCodes.includes(nace.code)
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="font-mono text-xs text-muted-foreground mr-1.5 shrink-0">
                              {nace.code}
                            </span>
                            <span className="truncate text-xs">
                              {nace.description}
                            </span>
                            <FacetCount count={facetCount('nace', nace.code)} />
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Tags (products, risks, opportunities) */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('tags')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.tags', {
            defaultValue: 'Tags',
          })}
        </FilterLabel>
        <Popover
          open={lockOf('tags') ? false : tagsOpen}
          onOpenChange={(open) => {
            setTagsOpen(open);
            if (!open) setTagSearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('tags')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {tags.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.tagsPlaceholder', {
                      defaultValue: 'e.g. Software, Supply chains, Consulting…',
                    })}
                  </span>
                ) : (
                  tags.map((kw) => (
                    <Badge
                      key={kw}
                      variant="secondary"
                      className="text-xs font-normal gap-1 pr-1 border border-foreground"
                    >
                      <span className="max-w-[220px] truncate">{kw}</span>
                      <X
                        size={12}
                        className="cursor-pointer hover:bg-muted rounded-sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          removeSet(kw, setTags);
                        }}
                      />
                    </Badge>
                  ))
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                className={INPUT_PLACEHOLDER_CLASS}
                placeholder={t('search.sidebar.searchTags', {
                  defaultValue: 'Search tags...',
                })}
                value={tagSearch}
                onValueChange={setTagSearch}
              />
              <CommandList className="max-h-[272px]">
                <CommandEmpty>
                  {t('search.sidebar.noTagFound', {
                    defaultValue: 'No tag found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {tagList
                    .filter(
                      (kw) =>
                        !tagSearch ||
                        kw.toLowerCase().includes(tagSearch.toLowerCase()),
                    )
                    .slice(0, 100)
                    .map((kw) => (
                      <CommandItem
                        key={kw}
                        value={kw}
                        onSelect={() => toggleSet(kw, setTags)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            tags.includes(kw) ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="truncate text-xs">{kw}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* CPV-Code */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('cpvCode')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.cpvCode', { defaultValue: 'CPV code' })}
        </FilterLabel>
        <Popover
          open={lockOf('cpvCode') ? false : cpvCodesOpen}
          onOpenChange={(open) => {
            setCpvCodesOpen(open);
            if (!open) setCpvSearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('cpvCode')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {!cpvCode ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.cpvCodePlaceholder', {
                      defaultValue: 'e.g. 45000000 Construction work',
                    })}
                  </span>
                ) : (
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal gap-1 pr-1 border border-foreground"
                  >
                    <span className="font-mono text-muted-foreground">
                      {cpvCode}
                    </span>
                    <span className="max-w-[180px] truncate">
                      {cpvCodesList.find((c) => c.code === cpvCode)
                        ?.description || ''}
                    </span>
                    <X
                      size={12}
                      className="cursor-pointer hover:bg-muted rounded-sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setCpvCode('');
                      }}
                    />
                  </Badge>
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                placeholder={t('search.sidebar.searchCpvCodes', {
                  defaultValue: 'Search CPV codes (min. 2 digits)…',
                })}
                value={cpvSearch}
                onValueChange={(v) => {
                  setCpvSearch(v);
                  if (cpvListRef.current) cpvListRef.current.scrollTop = 0;
                }}
              />
              <CommandList
                ref={cpvListRef}
                className="h-[272px] overflow-y-auto"
              >
                <CommandEmpty>
                  {t('search.sidebar.noCpvFound', {
                    defaultValue: 'No CPV code found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {cpvCodesList.map((cpv) => (
                    <CommandItem
                      key={cpv.code}
                      value={`${cpv.code} ${cpv.description}`}
                      onSelect={() => {
                        setCpvCode(cpv.code);
                        setCpvCodesOpen(false);
                        setCpvSearch('');
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          cpvCode === cpv.code ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="font-mono text-xs text-muted-foreground mr-1.5">
                        {cpv.code}
                      </span>
                      <span className="truncate text-xs">
                        {cpv.description}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </FilterGroup>
  );
}
